<template>
    <a-modal
        :visible="visible"
        :title="'Pivot Ayarları - ' + widget.title"
        width="90%"
        :footer="null"
        @cancel="$emit('close')"
        class="pivot-settings-modal"
        :destroyOnClose="true"
    >
        <div class="settings-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="left-actions">
                    <!-- Search Input -->
                    <a-input-search
                        v-model="searchQuery"
                        placeholder="Ara..."
                        style="width: 250px"
                        @search="handleSearch"
                        allowClear
                    />

                    <!-- Filter Button with Popover -->
                    <a-popover
                        placement="bottomLeft"
                        trigger="click"
                        v-model="filterVisible"
                        overlayClassName="table-filter-popover"
                    >
                        <template slot="content">
                            <TableFilter :fields="fields" :value="filters" @input="onFiltersUpdated" />
                        </template>
                        <SupsisButton :classNames="`${filters.length > 0 ? 'active-filters' : ''}`">
                            <i class="material-icons">filter_alt</i>
                            Filtre
                            <span v-if="filters.length > 0" class="filter-count">
                                {{ filters.length }}
                            </span>
                            <i class="material-icons clear-filters" @click.stop="clearFilters" v-if="filters.length"
                                >close</i
                            >
                        </SupsisButton>
                    </a-popover>
                </div>
            </div>

            <!-- Divider -->
            <a-divider style="margin: 12px 0" />

            <!-- Content Section -->
            <div class="content-section">
                <div class="preview-section">
                    <div v-if="!previewData" class="loading-state">
                        <a-spin />
                        <div>Pivot yükleniyor...</div>
                    </div>
                    <div v-else-if="previewData.error" class="error-state">
                        <i class="material-icons">error_outline</i>
                        <div>{{ previewData.message }}</div>
                    </div>
                    <pivot-table 
                        v-else 
                        :data="previewData" 
                        :config="previewSettings" 
                        :metadata="previewMetadata"
                        :value-fields="previewSettings.valueFields"
                    />
                </div>

                <!-- Vertical Divider -->
                <a-divider type="vertical" style="height: 100%; margin: 0 12px" />

                <div class="settings-section">
                    <a-form :form="form" layout="vertical">
                        <!-- Row Fields -->
                        <a-collapse :defaultActiveKey="['1']">
                            <a-collapse-panel key="1" header="Satır Alanları">
                                <pivot-field-selector
                                    title="Satır Alanları"
                                    :fields="fields"
                                    v-model="previewSettings.rowFields"
                                    :allow-multiple="true"
                                    @change="handleConfigChange"
                                />
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Column Fields -->
                        <a-collapse style="margin-top: 12px">
                            <a-collapse-panel key="1" header="Sütun Alanları">
                                <pivot-field-selector
                                    title="Sütun Alanları"
                                    :fields="fields"
                                    v-model="previewSettings.columnFields"
                                    :allow-multiple="true"
                                    @change="handleConfigChange"
                                />
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Date Grouping -->
                        <a-collapse v-if="hasDateFields" style="margin-top: 12px">
                            <a-collapse-panel key="1" header="Tarih Gruplandırma">
                                <div class="date-grouping-section">
                                    <div 
                                        v-for="dateField in selectedDateFields" 
                                        :key="'date-group-' + dateField._id"
                                        class="date-grouping-item"
                                    >
                                        <div class="field-label">{{ dateField.name }}</div>
                                        <a-select
                                            :value="getDateGrouping(dateField._id)"
                                            @change="(val) => setDateGrouping(dateField._id, val)"
                                            style="width: 100%"
                                            placeholder="Gruplandırma seçin"
                                        >
                                            <a-select-option value="day">Günlük</a-select-option>
                                            <a-select-option value="week">Haftalık</a-select-option>
                                            <a-select-option value="month">Aylık</a-select-option>
                                            <a-select-option value="quarter">3 Aylık</a-select-option>
                                            <a-select-option value="year">Yıllık</a-select-option>
                                        </a-select>
                                    </div>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Value Fields -->
                        <a-collapse style="margin-top: 12px">
                            <a-collapse-panel key="1" header="Değer Alanları">
                                <div class="value-fields-container">
                                    <div
                                        v-for="(valueField, index) in previewSettings.valueFields"
                                        :key="'value-' + index"
                                        class="value-field-item"
                                    >
                                        <div class="value-field-config">
                                            <div class="field-row">
                                                <a-select
                                                    :value="valueField.fieldId"
                                                    @change="(val) => onValueFieldChange(index, val)"
                                                    style="flex: 1"
                                                    placeholder="Alan seçiniz"
                                                >
                                                    <a-select-option
                                                        v-for="field in numericFields"
                                                        :key="field._id"
                                                        :value="field._id"
                                                    >
                                                        {{ field.name }}
                                                    </a-select-option>
                                                </a-select>

                                                <a-select
                                                    :value="valueField.aggregation"
                                                    @change="(val) => onAggregationChange(index, val)"
                                                    style="width: 150px"
                                                >
                                                    <a-select-option value="sum">Toplam</a-select-option>
                                                    <a-select-option value="count">Sayı</a-select-option>
                                                    <a-select-option value="average">Ortalama</a-select-option>
                                                    <a-select-option value="median">Medyan</a-select-option>
                                                    <a-select-option value="min">Minimum</a-select-option>
                                                    <a-select-option value="max">Maximum</a-select-option>
                                                    <a-select-option value="variance">Varyans</a-select-option>
                                                    <a-select-option value="stddev">Std. Sapma</a-select-option>
                                                    <a-select-option value="countDistinct">Benzersiz Sayı</a-select-option>
                                                </a-select>

                                                <a-button
                                                    type="link"
                                                    @click="removeValueField(index)"
                                                    v-if="previewSettings.valueFields.length > 1"
                                                >
                                                    <i class="material-icons">delete</i>
                                                </a-button>
                                            </div>
                                            
                                            <div class="label-row">
                                                <a-input
                                                    :value="valueField.label"
                                                    @change="(e) => onValueLabelChange(index, e.target.value)"
                                                    placeholder="Özel etiket (opsiyonel)"
                                                    size="small"
                                                >
                                                    <i slot="prefix" class="material-icons" style="font-size: 16px;">label</i>
                                                </a-input>
                                            </div>

                                            <div class="unit-row">
                                                <div class="unit-label">Birim:</div>
                                                <a-radio-group 
                                                    :value="getValueFieldUnit(index)" 
                                                    button-style="solid" 
                                                    size="small"
                                                >
                                                    <a-radio-button value="none" @click="handleValueUnitChange(index, 'none')">Yok</a-radio-button>
                                                    <a-radio-button value="₺" @click="handleValueUnitChange(index, '₺')">₺</a-radio-button>
                                                    <a-radio-button value="$" @click="handleValueUnitChange(index, '$')">$</a-radio-button>
                                                    <a-radio-button value="€" @click="handleValueUnitChange(index, '€')">€</a-radio-button>
                                                    <a-radio-button value="%" @click="handleValueUnitChange(index, '%')">%</a-radio-button>
                                                    <a-radio-button value="custom" @click="handleValueUnitChange(index, 'custom')">Özel</a-radio-button>
                                                </a-radio-group>
                                            </div>

                                            <div class="unit-config-row" v-if="getValueFieldUnit(index) === 'custom'">
                                                <a-input
                                                    :value="valueField.unit && valueField.unit.customValue"
                                                    @change="(e) => onValueCustomUnitChange(index, e.target.value)"
                                                    placeholder="Özel birim"
                                                    size="small"
                                                />
                                            </div>

                                            <div class="unit-position-row" v-if="getValueFieldUnit(index) !== 'none'">
                                                <div class="unit-label">Pozisyon:</div>
                                                <a-radio-group
                                                    :value="getValueFieldUnitPosition(index)"
                                                    @change="(e) => onValueUnitPositionChange(index, e.target.value)"
                                                    button-style="solid"
                                                    size="small"
                                                >
                                                    <a-radio-button value="left">Solda</a-radio-button>
                                                    <a-radio-button value="right">Sağda</a-radio-button>
                                                </a-radio-group>
                                            </div>
                                        </div>
                                    </div>

                                    <a-button type="dashed" block @click="addValueField" style="margin-top: 12px">
                                        <i class="material-icons">add</i>
                                        Değer Alanı Ekle
                                    </a-button>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Color Settings -->
                        <a-collapse style="margin-top: 12px">
                            <a-collapse-panel key="1" header="Renklendirme Ayarları">
                                <pivot-color-settings-panel
                                    v-model="previewSettings.colorSettings"
                                    @change="handleConfigChange"
                                />
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Display Options -->
                        <a-collapse style="margin-top: 12px">
                            <a-collapse-panel key="1" header="Görünüm Ayarları">
                                <a-form-item>
                                    <a-checkbox
                                        :checked="previewSettings.displayOptions.showTotals"
                                        @change="(e) => onDisplayOptionChange('showTotals', e.target.checked)"
                                    >
                                        Genel Toplamları Göster
                                    </a-checkbox>
                                </a-form-item>

                                <a-form-item>
                                    <a-checkbox
                                        :checked="previewSettings.displayOptions.showSubtotals"
                                        @change="(e) => onDisplayOptionChange('showSubtotals', e.target.checked)"
                                    >
                                        Alt Toplamları Göster
                                    </a-checkbox>
                                </a-form-item>

                                <a-form-item label="Ondalık Basamak">
                                    <a-input-number
                                        :value="previewSettings.displayOptions.decimalFormat"
                                        @change="(val) => onDisplayOptionChange('decimalFormat', val)"
                                        :min="0"
                                        :max="10"
                                        style="width: 100px"
                                    />
                                </a-form-item>

                                <a-form-item label="Boş Hücre Gösterimi">
                                    <a-input
                                        :value="previewSettings.displayOptions.emptyCell"
                                        @change="(e) => onDisplayOptionChange('emptyCell', e.target.value)"
                                        style="width: 100px"
                                    />
                                </a-form-item>

                                <a-form-item>
                                    <a-checkbox
                                        :checked="previewSettings.displayOptions.compactMode"
                                        @change="(e) => onDisplayOptionChange('compactMode', e.target.checked)"
                                    >
                                        Kompakt Mod
                                    </a-checkbox>
                                </a-form-item>
                            </a-collapse-panel>
                        </a-collapse>
                    </a-form>
                </div>
            </div>

            <!-- Kaydet/İptal Butonları -->
            <div class="form-actions">
                <a-button @click="$emit('close')">İptal</a-button>
                <a-button type="primary" @click="handleSave" :loading="saving"> Kaydet </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { TABLE_API } from "../../../../../../api/table";
import PivotTable from "./components/PivotTable.vue";
import PivotFieldSelector from "./components/PivotFieldSelector.vue";
import PivotColorSettingsPanel from "./components/PivotColorSettingsPanel.vue";
import TableFilter from "../../components/filter/TableFilter.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import log2 from "../../../../../../utils/log2";

const log = log2("PivotSettingsModal");

export default Vue.extend({
    name: "PivotSettingsModal",
    components: {
        PivotTable,
        PivotFieldSelector,
        PivotColorSettingsPanel,
        TableFilter,
        SupsisButton,
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        widget: {
            type: Object,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({ filters: [], searchQuery: "" }),
        },
    },
    data() {
        const defaultSettings = this.getDefaultSettings();
        const widgetSettings = this.widget.settings || {};
        
        return {
            form: this.$form.createForm(this),
            previewSettings: _.merge({}, defaultSettings, widgetSettings),
            filters: _.cloneDeep((widgetSettings.filters) || []),
            searchQuery: _.cloneDeep((widgetSettings.searchQuery) || ""),
            previewData: null,
            saving: false,
            filterVisible: false
        };
    },
    computed: {
        numericFields() {
            return this.fields.filter(
                (field) => ["number", "currency", "percent"].includes(field.fieldType) || field.valueType === "number"
            );
        },
        previewMetadata() {
            if (!this.previewData || !this.previewData.metadata) {
                return { min: 0, max: 0 };
            }
            return this.previewData.metadata;
        },
        hasDateFields() {
            const allSelectedFields = [
                ...(this.previewSettings.rowFields || []),
                ...(this.previewSettings.columnFields || [])
            ];
            return allSelectedFields.some(fieldId => {
                const field = this.fields.find(f => f._id === fieldId);
                return field && field.fieldType === 'datetime';
            });
        },
        selectedDateFields() {
            const allSelectedFields = [
                ...(this.previewSettings.rowFields || []),
                ...(this.previewSettings.columnFields || [])
            ];
            return allSelectedFields
                .map(fieldId => this.fields.find(f => f._id === fieldId))
                .filter(field => field && field.fieldType === 'datetime');
        }
    },
    methods: {
        getDefaultSettings() {
            return {
                rowFields: [],
                columnFields: [],
                valueFields: [
                    {
                        fieldId: null,
                        aggregation: "sum",
                    },
                ],
                colorSettings: {
                    heatMap: {
                        enabled: false,
                        scale: "green-yellow-red",
                        minColor: "#00ff00",
                        maxColor: "#ff0000",
                        ignoreZero: true
                    },
                    manualRules: [],
                },
                displayOptions: {
                    showTotals: true,
                    showSubtotals: true,
                    decimalFormat: 2,
                    emptyCell: "-",
                    compactMode: false
                },
                dateGrouping: {}, // fieldId: 'day'|'week'|'month'|'quarter'|'year'
                filters: [],
                searchQuery: "",
            };
        },
        handleConfigChange() {
            this.loadPreviewData();
        },
        onValueFieldChange(index, fieldId) {
            this.previewSettings.valueFields[index].fieldId = fieldId;
            this.handleConfigChange();
        },
        onAggregationChange(index, aggregation) {
            this.previewSettings.valueFields[index].aggregation = aggregation;
            // Eğer custom label yoksa, aggregation değiştiğinde otomatik label güncelle
            if (!this.previewSettings.valueFields[index].label) {
                this.$delete(this.previewSettings.valueFields[index], 'label');
            }
            this.handleConfigChange();
        },
        onValueLabelChange(index, label) {
            if (label && label.trim()) {
                this.$set(this.previewSettings.valueFields[index], 'label', label.trim());
            } else {
                this.$delete(this.previewSettings.valueFields[index], 'label');
            }
            this.handleConfigChange();
        },
        addValueField() {
            this.previewSettings.valueFields.push({
                fieldId: null,
                aggregation: "sum",
                label: null,
                unit: {
                    value: 'none',
                    position: 'right',
                    customValue: ''
                }
            });
        },
        removeValueField(index) {
            this.previewSettings.valueFields.splice(index, 1);
            this.handleConfigChange();
        },
        onDisplayOptionChange(key, value) {
            this.previewSettings.displayOptions[key] = value;
            this.handleConfigChange();
        },
        loadPreviewData: _.debounce(async function () {
            try {
                if (!this.previewSettings.rowFields || this.previewSettings.rowFields.length === 0) {
                    this.previewData = null;
                    return;
                }
                if (!this.previewSettings.valueFields || this.previewSettings.valueFields.length === 0) {
                    this.previewData = null;
                    return;
                }

                const customWidgetSettings = this.generateCustomWidgetSettings();
                this.previewData = await TABLE_API.getPivotData(
                    this.table._id,
                    this.widget._id,
                    this.upstreamFilters,
                    customWidgetSettings
                );
            } catch (error) {
                console.error("Pivot verisi yüklenirken hata:", error);
                this.previewData = {
                    error: true,
                    message: "Pivot verisi yüklenirken hata oluştu",
                };
            }
        }, 500),
        generateCustomWidgetSettings() {
            return Object.assign({}, this.previewSettings, {
                filters: this.filters,
                searchQuery: this.searchQuery,
            });
        },
        async handleSave() {
            try {
                this.saving = true;
                const updatedWidget = Object.assign({}, this.widget.settings, this.previewSettings, {
                    filters: this.filters,
                    searchQuery: this.searchQuery,
                });
                await this.$emit("save", updatedWidget);
                this.$emit("close");
            } catch (error) {
                this.$message.error("Ayarlar kaydedilirken bir hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        onFiltersUpdated(newFilters) {
            this.filters = newFilters;
            this.loadPreviewData();
        },
        clearFilters() {
            this.filters = [];
            this.loadPreviewData();
        },
        handleSearch(value) {
            this.searchQuery = value;
            this.loadPreviewData();
        },
        getDateGrouping(fieldId) {
            if (!this.previewSettings.dateGrouping) {
                this.$set(this.previewSettings, 'dateGrouping', {});
            }
            return this.previewSettings.dateGrouping[fieldId] || null;
        },
        setDateGrouping(fieldId, groupingType) {
            if (!this.previewSettings.dateGrouping) {
                this.$set(this.previewSettings, 'dateGrouping', {});
            }
            if (groupingType) {
                this.$set(this.previewSettings.dateGrouping, fieldId, groupingType);
            } else {
                this.$delete(this.previewSettings.dateGrouping, fieldId);
            }
            this.handleConfigChange();
        },
        getValueFieldUnit(index) {
            const valueField = this.previewSettings.valueFields[index];
            if (!valueField.unit) {
                this.$set(valueField, 'unit', { value: 'none', position: 'right', customValue: '' });
            }
            const unitValue = valueField.unit.value;
            const isCustom = unitValue && !['none', '₺', '$', '€', '%'].includes(unitValue);
            return isCustom ? 'custom' : unitValue;
        },
        getValueFieldUnitPosition(index) {
            const valueField = this.previewSettings.valueFields[index];
            return (valueField.unit && valueField.unit.position) || 'right';
        },
        handleValueUnitChange(index, value) {
            const valueField = this.previewSettings.valueFields[index];
            if (!valueField.unit) {
                this.$set(valueField, 'unit', { value: 'none', position: 'right', customValue: '' });
            }
            
            if (value === 'none') {
                this.$set(valueField.unit, 'value', 'none');
            } else if (value === 'custom') {
                this.$set(valueField.unit, 'value', valueField.unit.customValue || '');
            } else {
                this.$set(valueField.unit, 'value', value);
            }
            this.handleConfigChange();
        },
        onValueCustomUnitChange(index, value) {
            const valueField = this.previewSettings.valueFields[index];
            if (!valueField.unit) {
                this.$set(valueField, 'unit', { value: '', position: 'right', customValue: '' });
            }
            this.$set(valueField.unit, 'customValue', value);
            this.$set(valueField.unit, 'value', value);
            this.handleConfigChange();
        },
        onValueUnitPositionChange(index, position) {
            const valueField = this.previewSettings.valueFields[index];
            if (!valueField.unit) {
                this.$set(valueField, 'unit', { value: 'none', position: 'right', customValue: '' });
            }
            this.$set(valueField.unit, 'position', position);
            this.handleConfigChange();
        },
    },
    async created() {
        try {
            const defaultSettings = this.getDefaultSettings();
            const widgetSettings = this.widget.settings || {};
            
            this.previewSettings = _.merge({}, defaultSettings, widgetSettings);
            this.filters = _.cloneDeep((widgetSettings.filters) || []);
            this.searchQuery = _.cloneDeep((widgetSettings.searchQuery) || "");
            await this.loadPreviewData();
        } catch (error) {
            console.error("Pivot verisi yüklenirken hata:", error);
        }
    },
    watch: {
        previewSettings: {
            handler: async function () {
                await this.loadPreviewData();
            },
            deep: true,
            immediate: true,
        },
        upstreamFilters: {
            handler: async function () {
                await this.loadPreviewData();
            },
            deep: true,
        },
    },
});
</script>

<style lang="scss">
.pivot-settings-modal {
    .settings-container {
        display: flex;
        flex-direction: column;
        height: calc(90vh - 150px);

        .header-section {
            padding: 0 0 12px 0;
            .left-actions {
                display: flex;
                gap: 12px;
                align-items: center;
            }
        }

        .filter-count {
            margin-left: 4px;
            background-color: rgba(0, 0, 0, 0.2);
            padding: 2px 4px;
            border-radius: 4px;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
        }

        .clear-filters {
            font-size: 14px;
            margin-left: 2px;
            cursor: pointer;
        }

        .content-section {
            display: flex;
            flex: 1;
            min-height: 0;

            .preview-section {
                flex: 1;
                min-width: 0;
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                padding: 16px;
                overflow: auto;
                height: 100%;

                .loading-state,
                .error-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    color: rgba(0, 0, 0, 0.45);
                    justify-content: center;
                    height: 100%;

                    .material-icons {
                        font-size: 24px;
                    }
                }
            }

            .settings-section {
                width: 400px;
                overflow-y: auto;
                padding: 16px;
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
            }
        }

        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 12px 0;
        }

        .value-fields-container {
            .value-field-item {
                margin-bottom: 16px;
                padding: 12px;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                background: #fafafa;

                .value-field-config {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .field-row {
                        display: flex;
                        gap: 8px;
                        align-items: center;
                    }

                    .label-row {
                        display: flex;
                        gap: 8px;
                    }

                    .unit-row {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        
                        .unit-label {
                            min-width: 50px;
                            font-size: 12px;
                            color: #666;
                        }
                    }

                    .unit-config-row {
                        display: flex;
                        gap: 8px;
                    }

                    .unit-position-row {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        
                        .unit-label {
                            min-width: 50px;
                            font-size: 12px;
                            color: #666;
                        }
                    }
                }
            }
        }

        .date-grouping-section {
            .date-grouping-item {
                margin-bottom: 16px;
                
                &:last-child {
                    margin-bottom: 0;
                }

                .field-label {
                    font-weight: 600;
                    font-size: 13px;
                    color: #333;
                    margin-bottom: 8px;
                }
            }
        }
    }
}
</style>
